import React, { useState, useEffect, useCallback, useMemo } from "react";
import ReactDOM from "react-dom";
import { useTranslation } from "react-i18next";
import Loadable from "@react-loadable/revised";
import PropTypes from "prop-types";
import fp from "lodash/fp";
import { useDispatch } from "react-redux";
import { bindActionCreators } from "redux";
import { useHistory, useLocation } from "react-router-dom";

import { LIST_VIEW_TYPE, UNCATEGORIZED_ID, GROUPING_VIEW_TYPE } from "../../../../constants";
import { siteMode } from "../../../../cores/siteMode";

import { assetActions } from "../../../../modules";
import {
    IconArchive,
    IconFileCopyto,
    IconDelete,
    IconEditLarge,
    IconMove,
    IconPhotoAdd,
    IconShareLink,
} from "@mzc-cloudplex/icons";
import menuService from "@services/menuService";

const LoadableSelectCategoriesModal = Loadable({
    loader: () => import("../../../../components/modals/SelectCategories"),
    loading: () => null,
});

const LoadableDeleteAssetWithReferenceModal = Loadable({
    loader: () => import("../../ui/DeleteAssetWithReferenceModal"),
    loading: () => null,
});

const LoadableShareModal = Loadable({
    loader: () => import("../../ui/ShareModal"),
    loading: () => null,
});

const LoadableCreatePhotoModal = Loadable({
    loader: () => import("../../ui/CreatePhotoModal"),
    loading: () => null,
});

const LoadableRenameModal = Loadable({
    loader: () => import("../../../../components/modals/Rename"),
    loading: () => null,
});

const LoadableDownloadLinkModal = Loadable({
    loader: () => import("@components/modals/DownloadLinkModal"),
    loading: () => null,
});

const LoadableSelectFolderPathModal = Loadable({
    loader: () => import("@components/modals/SelectFolderPath"),
    loading: () => null,
});

/**
 * 아이템들을 move & copy & delete 할 수 있는 컴포넌트
 * @param {string} activeView - 활성화된 view type
 * @param {object} hierarchyCategory - Hierarchy view에서 현재 활성화 된 item
 * @param {array} selectedItems - 체크된 아이템 요소들
 * @param {number} total - List item length
 * @param {string} type - LNB type
 * @param {func} onChangeCategoriesCopyToItems - Copy to items
 * @param {func} onChangeCategoriesMoveToItems - Move to Items
 * @param {func} onChangeAllSelectedItems - 전체 선택 / 해제
 * @param {func} onClickShare - share items
 * @param {object} activeBtns - view에서 버튼 노출 여부 {key: value, value: boolean} ex) value = 'copy', 'move', 'delete', 'share'
 */
function ControlStatus({
    activeView,
    hierarchyCategory,
    hierarchyType,
    selectedItems,
    selectedFolders,
    total,
    type,
    afterMutationWorks,
    onClickRename,
    onClickFolderRename,
    onChangeCategoriesCopyToItems,
    onChangeCategoriesMoveToItems,
    onChangeAllSelectedItems,
    onClickShare,
    onClickDeleteAll,
    onClickDeleteOne,
    onClickDownload,
    onClickChangeAssetsFolder,
    onDeleteEnd,
    activeBtns,
    checkable = true,
    showArchiveAssetModal,
}) {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const history = useHistory();
    const { pathname } = useLocation();
    const { spaceId, projectId } = menuService.parse(pathname);

    const [isShowCopyToModal, setIsShowCopyToModal] = useState(false);
    const [isShowMoveToModal, setIsShowMoveToModal] = useState(false);
    const [isShowMoveFolderModal, setIsShowMoveFolderModal] = useState(false);
    const [isShowRenameModal, setIsShowRenameModal] = useState(false);
    const [isShowDeleteConfirmModal, setIsShowDeleteConfirmModal] = useState(false);
    const [isShowShareToModal, setIsShowShareToModal] = useState(false);
    const [isShowCreatePhotoModal, setIsShowCreatePhotoModal] = useState(false);
    const [selectedImageItems, setSelectedImageItems] = useState(selectedItems);
    const [changedSelectedFolders, setChangedSelectedFolders] = useState();
    const [isShowDownloadLinkModal, setIsShowDownloadLinkModal] = useState(false);
    // const [isShowArchiveAssetModal, setIsShowArchiveAssetModal] = useState(false);

    const AssetActions = bindActionCreators(assetActions, dispatch);

    const isChecked = useMemo(
        () => (selectedFolders && selectedFolders.length) || (selectedItems && selectedItems.length),
        [selectedItems, selectedFolders],
    );

    useEffect(() => {
        let imageTypeItems = [];
        if (selectedItems) {
            imageTypeItems = selectedItems.filter((v) => v.mediaType === "IMAGE");
        }
        setSelectedImageItems(imageTypeItems);
    }, [selectedItems]);

    useEffect(() => {
        if (selectedFolders.length == 0) return;

        const newSelectedFolders = selectedFolders;

        newSelectedFolders[0].isFolder = true;
        newSelectedFolders[0].path = selectedFolders[0].name;

        setChangedSelectedFolders(newSelectedFolders);
    }, [selectedFolders]);

    const handleClickImageAssetToPhoto = useCallback(async () => {
        const [item] = selectedImageItems;

        const response = await AssetActions.getAsset(item.id);
        if (!response?.data) {
            return;
        }

        const photoAsset = response.data;
        history.push(`${menuService.paths.contentsPhotoCreate(spaceId, projectId)}`, {
            photoAsset,
        });
    }, [spaceId, projectId]);

    return (
        <React.Fragment>
            {/* 복사 */}
            {isShowCopyToModal
                ? ReactDOM.createPortal(
                      <LoadableSelectCategoriesModal
                          submitText={t(`common::label::Copy`)}
                          title={t(`common::label::Copy to`)}
                          type={type}
                          onModalClose={() => setIsShowCopyToModal(false)}
                          onSelectedCategories={(option) => {
                              setIsShowCopyToModal(false);
                              onChangeCategoriesCopyToItems(option);
                          }}
                      />,
                      document.body,
                  )
                : ""}
            {/* 이동 */}
            {isShowMoveToModal &&
                ReactDOM.createPortal(
                    <LoadableSelectCategoriesModal
                        submitText={`Move`}
                        title={`Move`}
                        type={type}
                        onModalClose={() => setIsShowMoveToModal(false)}
                        onSelectedCategories={(option) => {
                            setIsShowMoveToModal(false);
                            onChangeCategoriesMoveToItems(option);
                        }}
                    />,
                    document.body,
                )}
            {isShowMoveFolderModal &&
                ReactDOM.createPortal(
                    <LoadableSelectFolderPathModal
                        open={true}
                        domain={"Asset"}
                        submitText={`Move`}
                        title={`Move`}
                        onSubmit={async ({ folderId }) => {
                            //동작
                            await onClickChangeAssetsFolder(folderId);
                            setIsShowMoveFolderModal(false);
                        }}
                        onClose={() => {
                            setIsShowMoveFolderModal(false);
                        }}
                    />,
                    document.body,
                )}
            {/* 이름 변경 */}
            {isShowRenameModal &&
                selectedItems.length === 1 &&
                ReactDOM.createPortal(
                    <LoadableRenameModal
                        selectedItems={selectedItems}
                        onClickClose={() => {
                            setIsShowRenameModal(false);
                        }}
                        onClickSubmit={onClickRename}
                        // onClickSubmit={this.onClickRenameSource}
                        // onValidation={this.onValidation}
                        afterMutationWorks={afterMutationWorks}
                    />,
                    document.body,
                )}
            {isShowDeleteConfirmModal &&
                ReactDOM.createPortal(
                    <LoadableDeleteAssetWithReferenceModal
                        activeView={activeView}
                        hierarchyCategorySelected={
                            activeView === LIST_VIEW_TYPE.LIST ||
                            hierarchyType === GROUPING_VIEW_TYPE.FOLDER ||
                            fp.get("selected.id", hierarchyCategory) === UNCATEGORIZED_ID
                                ? {}
                                : fp.getOr({}, "selected", hierarchyCategory)
                        }
                        selectedItems={selectedItems}
                        categoryDeleteCheck={true}
                        onClickClose={() => setIsShowDeleteConfirmModal(false)}
                        onClickDelete={(item, isEndItem, isCategoryDelete) => {
                            console.log("onClickDelete", item, isEndItem, isCategoryDelete);
                            if (onClickDeleteOne) return onClickDeleteOne(item, isEndItem, isCategoryDelete);
                        }}
                        onClickDeleteAll={(remainItems, isCategoryDelete) => {
                            console.log("remainItems", remainItems, isCategoryDelete);
                            if (onClickDeleteAll) return onClickDeleteAll(remainItems, isCategoryDelete);
                        }}
                        onDeleteEnd={() => {
                            if (onDeleteEnd) onDeleteEnd();
                            setIsShowDeleteConfirmModal(false);
                        }}
                        isCategoryDelete={
                            activeView?.toLowerCase() === LIST_VIEW_TYPE.HIERARCHY &&
                            hierarchyCategory?.selected?.id &&
                            fp.get("selected.id", hierarchyCategory) !== UNCATEGORIZED_ID
                        }
                    />,
                    document.body,
                )}
            {/* 공유 */}
            {isShowShareToModal &&
                ReactDOM.createPortal(
                    <LoadableShareModal
                        selectedItems={selectedItems}
                        onClickClose={() => setIsShowShareToModal(false)}
                        onSubmit={(object) => {
                            setIsShowShareToModal(false);
                            onClickShare(object);
                        }}
                    />,
                    document.body,
                )}
            {/* 포토 생성 */}
            {isShowCreatePhotoModal &&
                ReactDOM.createPortal(
                    <LoadableCreatePhotoModal
                        selectedItems={selectedItems}
                        onClickClose={() => setIsShowCreatePhotoModal(false)}
                        onSubmit={() => {
                            setIsShowCreatePhotoModal(false);
                        }}
                    />,
                    document.body,
                )}
            {/*  다운로드 링크 */}
            {isShowDownloadLinkModal &&
                ReactDOM.createPortal(
                    <LoadableDownloadLinkModal
                        domain={"ASSET"}
                        selectedItems={selectedItems}
                        onClickClose={() => setIsShowDownloadLinkModal(false)}
                        onSubmit={(payload) => {
                            setIsShowDownloadLinkModal(false);
                            // this.onClickShare(payload);
                        }}
                    />,
                    document.body,
                )}

            {checkable && (
                <React.Fragment>
                    {siteMode.get() === "CONSOLE" ? (
                        <div className={`control-status ${selectedItems.length > 0 && "active"}`}>
                            <label
                                htmlFor="check"
                                className={`form-checkbox ${
                                    selectedItems && selectedItems.length === total ? "" : "checked-half"
                                }`}
                            >
                                <input
                                    type="checkbox"
                                    checked={isChecked}
                                    onChange={() => {
                                        onChangeAllSelectedItems(!isChecked);
                                    }}
                                />
                                <i />
                            </label>
                            <hr />

                            {selectedItems.length > 0 && (
                                <React.Fragment>
                                    {/* 비디오 생성 TOBE */}
                                    {/* <button
                                        type="button"
                                        className="btn btn-icon btn-sm btn-ghost"
                                        data-tooltip={t(`common::label::${"Create {{name}}"}`, {
                                            name: t(`common::label::Video`),
                                        })}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                        }}
                                    >
                                        <i className="sprite sprite-create-video" />
                                    </button> */}
                                    {/* // 비디오 생성  */}
                                    {/* 포토 생성  */}
                                    {activeBtns.createPhoto && (
                                        <React.Fragment>
                                            <button
                                                type="button"
                                                className="btn btn-icon btn-sm btn-ghost"
                                                data-tooltip={t(`common::label::${"Create {{name}}"}`, {
                                                    name: t(`common::label::Photo`),
                                                })}
                                                disabled={selectedItems.find((v) => v.mediaType !== "IMAGE")}
                                                onClick={(e) => {
                                                    e.stopPropagation();

                                                    if (selectedItems.length === 1) {
                                                        handleClickImageAssetToPhoto();
                                                        return;
                                                    }

                                                    if (selectedItems && selectedItems.length) {
                                                        setIsShowCreatePhotoModal(true);
                                                    }
                                                }}
                                            >
                                                <IconPhotoAdd size={16} />
                                            </button>
                                            <hr />
                                        </React.Fragment>
                                    )}
                                    {/*// 포토 생성  */}
                                    {/* 외부 공유 */}
                                    <button
                                        type="button"
                                        className="btn btn-icon btn-sm btn-ghost"
                                        data-tooltip={`${t("common::label::Share link")}`}
                                        disabled={!(selectedItems && selectedItems.length)}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            setIsShowDownloadLinkModal(true);
                                        }}
                                    >
                                        <IconShareLink size={16} />
                                    </button>
                                    <hr />

                                    {hierarchyType === "category" && activeBtns.copy && (
                                        <>
                                            <button
                                                type="button"
                                                className="btn btn-icon btn-sm btn-ghost"
                                                data-tooltip={`${t("common::label::Copy to")}`}
                                                disabled={!(selectedItems && selectedItems.length)}
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    if (selectedItems && selectedItems.length) {
                                                        setIsShowCopyToModal(true);
                                                    }
                                                }}
                                            >
                                                <IconFileCopyto size={16} />
                                            </button>
                                        </>
                                    )}
                                    {activeBtns.move && (
                                        <>
                                            <button
                                                type="button"
                                                className="btn btn-icon btn-sm btn-ghost"
                                                data-tooltip={`${t("common::label::Move")}`}
                                                disabled={!(selectedItems && selectedItems.length)}
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    if (selectedItems && selectedItems.length) {
                                                        if (hierarchyType === "category") setIsShowMoveToModal(true);
                                                        else setIsShowMoveFolderModal(true);
                                                    }
                                                }}
                                            >
                                                <IconMove size={16} />
                                            </button>
                                        </>
                                    )}
                                    {(selectedItems.length === 1 || selectedFolders.length === 1) && (
                                        <button
                                            type="button"
                                            className="btn btn-icon btn-sm btn-ghost"
                                            data-tooltip={`${t("common::label::Rename", `Rename`)}`}
                                            // disabled={}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                setIsShowRenameModal(true);
                                            }}
                                        >
                                            <IconEditLarge size={16} />
                                        </button>
                                    )}
                                    <hr />
                                    {/* 다운로드 TOBE */}
                                    <button
                                        type="button"
                                        className="btn btn-icon btn-sm btn-ghost"
                                        data-tooltip={`${t("common::label::Download")}`}
                                        disabled={!(selectedItems && selectedItems.length)}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            onClickDownload();
                                        }}
                                    >
                                        <i className="sprite sprite-download" />
                                    </button>
                                    <hr />
                                    {/* //다운로드 */}
                                    {/* <button
                                        type="button"
                                        className="btn btn-icon btn-sm btn-ghost"
                                        data-tooltip={`${t("common::label::Archive")}`}
                                        disabled={!(selectedItems && selectedItems.length)}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                        }}
                                    >
                                        <i className="sprite sprite-archive" />
                                    </button>
                                    <hr />                                    */}
                                    {/* 삭제  */}
                                    {activeBtns.delete && (
                                        <button
                                            type="button"
                                            className="btn btn-icon btn-sm btn-ghost"
                                            data-tooltip={`${t("common::label::Delete")}`}
                                            disabled={!(selectedItems && selectedItems.length)}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                if (selectedItems && selectedItems.length) {
                                                    setIsShowDeleteConfirmModal(true);
                                                }
                                            }}
                                        >
                                            <IconDelete size={16} />
                                        </button>
                                    )}
                                    <hr />
                                    {/* 아카이브 에셋 */}
                                    {/* {activeBtns.archive && ( */}
                                    <button
                                        type="button"
                                        className="btn btn-icon btn-sm btn-ghost"
                                        data-tooltip={`${t("common::label::Archive")}`}
                                        disabled={!(selectedItems && selectedItems.length)}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            if (selectedItems && selectedItems.length) {
                                                showArchiveAssetModal(true);
                                            }
                                        }}
                                    >
                                        <IconArchive size={16} />
                                    </button>
                                    {/* )} */}
                                    <hr />
                                </React.Fragment>
                            )}
                        </div>
                    ) : (
                        <div className={"control-status"}>
                            <label
                                htmlFor="check"
                                className={`form-checkbox ${
                                    selectedItems && selectedItems.length === total ? "" : "checked-half"
                                }`}
                            >
                                <input
                                    type="checkbox"
                                    checked={isChecked}
                                    onChange={() => {
                                        onChangeAllSelectedItems(!isChecked);
                                    }}
                                />
                                <i />
                            </label>
                            <hr />
                        </div>
                    )}
                </React.Fragment>
            )}
        </React.Fragment>
    );
}

ControlStatus.propsType = {
    activeView: PropTypes.string,
    hierarchyCategory: PropTypes.object,
    selectedItems: PropTypes.array,
    total: PropTypes.number,
    type: PropTypes.string,
    onChangeCategoriesCopyToItems: PropTypes.func,
    onChangeCategoriesMoveToItems: PropTypes.func,
    onChangeAllSelectedItems: PropTypes.func,
    onClickShare: PropTypes.func,
    onClickDeleteAll: PropTypes.func,
    onClickDeleteOne: PropTypes.func,
    activeBtns: PropTypes.object,
    onClickFolderRename: PropTypes.func,
    onClickDownload: PropTypes.func,
};

export default React.memo(ControlStatus);
